import type { JSONContent } from "@tiptap/core";
import type { createManager } from "../chapter_markdown";

// A dice test in the short notation, `:::teste` (callout.ts owns the grammar). Lines between the dice and the
// outcomes are not editable here; `more` carries them through untouched. The editor never builds the long
// blockquote itself: it writes the short text and lets the serializer read it.
export interface DiceTest {
	title: string;
	dice: string;
	more: string[];
	op: "≤" | ">";
	value: string;
	pass: string;
	fail: string;
}

const SHORT = /^:::teste (\S.*?)[ \t]*\n([\s\S]*)\n:::\n?$/;

export const blankTest: DiceTest = { title: "", dice: "1D20", more: [], op: "≤", value: "", pass: "", fail: "" };

export function readTest(short: string): DiceTest | null {
	const m = SHORT.exec(short);
	const lines = m?.[2].split("\n") ?? [];
	const success = /^Sucesso (≤|>) (.+?): (.+)$/.exec(lines.at(-2) ?? "");
	const failure = /^Falha: (.+)$/.exec(lines.at(-1) ?? "");
	if (!m || lines.length < 3 || !success || !failure) return null;
	return {
		title: m[1],
		dice: lines[0],
		more: lines.slice(1, -2),
		op: success[1] as DiceTest["op"],
		value: success[2],
		pass: success[3],
		fail: failure[1],
	};
}

export function writeTest(test: DiceTest): string {
	return [`:::teste ${test.title}`, test.dice, ...test.more, `Sucesso ${test.op} ${test.value}: ${test.pass}`, `Falha: ${test.fail}`, ":::"].join("\n");
}

// The block the test stands for, or null unless the serializer writes it back as a `:::teste` that reads as the
// same fields (an empty field, a second line, a dice line that is not NdM, a ": " in the threshold): the form can
// only produce valid tests. A `:::teste` it cannot read comes back as a verbatim HTML block, hence the type check.
export function testNode(manager: ReturnType<typeof createManager>, test: DiceTest): JSONContent | null {
	const text = writeTest(test);
	const doc = manager.parse(text);
	const block = doc.content?.[0];
	const read = readTest(manager.serialize(doc));
	return block?.type === "blockquote" && JSON.stringify(read) === JSON.stringify(test) ? block : null;
}

export function shortOf(manager: ReturnType<typeof createManager>, node: JSONContent): string {
	return manager.serialize({ type: "doc", content: [node] });
}
